import React from 'react';
import PropTypes from 'prop-types';
import humps from 'humps';
import Text from 'relements/build/esm/input/text';
import Radio from 'relements/build/esm/input/radio';
import Chips from 'relements/build/esm/input/chips';
import Accordion from 'relements/build/esm/ui/accordion';
import HSL from '@haptik/helpers/build/esm/hslBuilder';
import HSLDisplay from '@haptik/helpers/build/esm/hslDisplay';
import OCDropdown from '@src/components/OCDropdown';
import Toggle from 'relements/build/esm/input/toggle';

import LanguageTab from '@src/components/LanguageTab/LanguageTab';
import BotStore from '@src/store';
import { dispatch } from '@src/dispatcher';
import { getTranslations } from '@src/api/DomainAPI';

// Components
import Icon from 'components/Icon';

// Constants
import {
  ENTITY_TYPES,
  ENTITY_META_DATA,
  ENTITY_SECURITY,
  ENTITY_CATEGORY,
  ENTITY_TYPE_REGEX,
} from '@src/constants/Entities';
import { BASE_URL } from '@src/const';

// Actions
import { createAlert } from '@src/actions/BotActions';

// Local
import {
  AlertTypes,
  ActionTypes,
  FEATURE_FLAG_DATA,
} from '@src/constants/MogamboConstants';
import isBotResponseEmpty from '@src/components/LanguageTab/Utils';
import EntityTypeCard from './EntityTypeCard';

import styles from './EntityInfo.scss';
import EntitySecurityLabel from '../EntitySearch/EntitySecurityLabel';

export default class EntityInfo extends React.Component {
  state = {
    entityRepromptVisible: true,
    advancedOptionsVisible: false,
    language: 'en',
    currentHybridLanguage: 'en',
    currentChannelId: 5,
    webSDKSaved: true,
    botResponsesWithChannel: {},
    loading: false,
    channelName: 'WebSDK',
  };

  handleHindiScriptChange = (script) => {
    this.setState({
      currentHybridLanguage: script,
    });
  };

  _handleOCDropdownChange = (id, channelName) => {
    const {
      isEdited,
      entityRepromptValue,
      entityRepromptValueWithChannel,
      updateCurrentChannelName,
    } = this.props;

    let translatePayload = false;
    const testConfigData = BotStore.getTestConfigData();
    const payload =
      humps.decamelizeKeys({
        ...entityRepromptValueWithChannel?.[this.state.language]?.[5],
      }) || {};

    const currentChannels = Object.keys(
      entityRepromptValueWithChannel?.[this.state.language]
    ).map((i) => Number(i));

    const { responses: variants = [] } =
      this.props.entityRepromptValue?.[this.state.language]?.[
        this.state.currentHybridLanguage
      ] || {};

    // If WebSDK does not contain variants
    if (!payload?.bot_says?.variants?.length) {
      createAlert(
        'Please add bot responses within WebSDK to proceed',
        3000,
        AlertTypes.NEGATIVE
      );
      return;
    }

    // If WebSDK responses are not saved
    if (
      !this.state.webSDKSaved ||
      (isEdited && this.state.currentChannelId === 5)
    ) {
      createAlert(
        'Please save bot responses in WebSDK to proceed',
        3000,
        AlertTypes.NEGATIVE
      );
      return;
    }

    this.setState({
      currentChannelId: id,
      channelName,
      loading: true,
    });
    updateCurrentChannelName(channelName);

    payload.domain_name = this.props.domainName;
    payload.target_device_platform_id = id;
    payload.business_id = testConfigData?.business_id;
    payload.client_id = testConfigData?.partner_client_id;

    if (!currentChannels.includes(id)) {
      translatePayload = true;
    }

    if (translatePayload) {
      getTranslations(payload).then(({ success, response }) => {
        if (success) {
          this.props.onTranslation(
            response.body,
            this.state.currentChannelId,
            () => {
              this.setState({ loading: false });
            }
          );
        }
      });
    } else {
      this.setState({ loading: false });
    }
  };

  renderBotResponses = () => {
    const { language, currentChannelId } = this.state;
    const { variants = [], quickResponses: quickReplies = [] } =
      this.props.entityRepromptValueWithChannel?.[language]?.[currentChannelId]
        ?.botSays || {};
    const isTranslated =
      parseInt(currentChannelId) !== 5 &&
      !this.props.dpsvChannels.includes(currentChannelId.toString());

    return (
      <div className={styles.botSaysBody}>
        {this.props.edit ? (
          <HSL
            label="Bot Response"
            value={variants}
            onChange={(value) => this._setRepromptState('responses', value)}
            placeholder="Add Element"
            withReplies
            withVariants
            channelsEnabled={['whatsapp']}
            isTranslated={isTranslated}
            currentChannelName={this.state.channelName}
            isRephraseEnabled={
              BotStore.getFeatureFlag(
                FEATURE_FLAG_DATA.FLAGS.ENABLE_REPHRASE_RESPONSES
              ) && language === 'en'
            }
            baseUrl={BASE_URL}
          />
        ) : (
          <HSLDisplay
            label="Bot Response"
            value={variants}
            withReplies
            withVariants
          />
        )}
        <Chips
          label="Quick Replies"
          placeholder="Add Quick Replies and press enter"
          value={quickReplies}
          disabled={!this.props.edit}
          onChange={(quickReplies) =>
            this._setRepromptState('quickResponses', quickReplies)
          }
          className={styles.botSaysRepliesChips}
        />
      </div>
    );
  };

  componentWillUnmount() {
    dispatch(ActionTypes.UPDATE_ENTITY_LANGUAGES, {
      language: 'en',
      hybridLanguage: 'en',
    });
  }

  render() {
    const {
      name,
      description,
      disableTranslation,
      entityCategory,
      payloadKey,
      contextKey,
      formKey,
      prevContextTags,
      entityType,
      personalData,
      entityRepromptValueWithChannel,
    } = this.props;
    const entitySecurity = personalData
      ? Object.values(ENTITY_SECURITY)[0]
      : Object.values(ENTITY_SECURITY)[1];

    const { loading, channelName, language, currentChannelId } = this.state;

    const { variants = [] } =
      entityRepromptValueWithChannel?.[language]?.[currentChannelId]?.botSays ||
      {};

    const showChannelChangeError =
      variants.length === 0 ||
      !this.state.webSDKSaved ||
      (this.props.isEdited && this.state.currentChannelId === 5);

    return (
      <div>
        <div className={styles.headerRow}>
          <Text
            disabled={!(this.props.edit && this.props.new)}
            label="Entity Name"
            placeholder="Name of the Entity"
            value={name}
            onChange={(value) => this.props.onEntityInfoChange('name', value)}
            className={styles.headerRowInput}
            hint="Only Lower case alphabets and underscores are allowed."
          />
          <Text
            disabled={!this.props.edit}
            label="Description of the entity"
            placeholder="Describe this Entity in a few words"
            value={description}
            onChange={(value) =>
              this.props.onEntityInfoChange('description', value)
            }
            className={styles.headerRowInput}
            hint="Help other bot builders understand this entity's purpose (Min 10 chars)"
          />
          {this.props.edit ? (
            <div>
              <span className={styles.entitySecurity}>User data privacy</span>
              <span className={styles.entitySecurityHint}>
                Type of user's data collected
              </span>
              <Radio
                label=""
                value={entitySecurity}
                options={ENTITY_SECURITY}
                className={styles.headerRowInput}
                onChange={(option) =>
                  this.props.onEntityInfoChange('personalData', option.value)
                }
              />
            </div>
          ) : (
            <div>
              <span className={styles.entitySecurity}>User data privacy</span>
              <span className={styles.entitySecurityHint}>
                Type of user's data collected
              </span>
              <EntitySecurityLabel security={entitySecurity.value} />
            </div>
          )}
        </div>
        {this.props.isSuperman && (
          <div>
            <Accordion useDragHandle>
              <Accordion.Item
                reorderDisabled
                key="entity-reprompt"
                index="entity-reprompt"
                active={this.state.entityRepromptVisible}
                onChange={this._toggleEntityReprompt}
                noPadding
                prefixClassName={styles.accordionItemHeight}
                renderHeader={() => (
                  <div className={styles.advancedOptionsHeader}>
                    <Icon iconType="gear" /> Entity Reprompt
                    <sup>&nbsp;*</sup>
                  </div>
                )}
              >
                {this.state.entityRepromptVisible && (
                  <div className={styles.botSays}>
                    <>
                      <LanguageTab
                        currentBotSaysLanguage={this.state.language}
                        currentHybridLanguage={this.state.currentHybridLanguage}
                        onLanguageChange={this._handleTabChange}
                        variant="desktop"
                      />
                      <OCDropdown
                        selectedChannel={this.state.currentChannelId}
                        showChannelChangeError={showChannelChangeError}
                        onOCDropDownChange={this._handleOCDropdownChange}
                      />
                      <div>
                        {!loading ? (
                          this.renderBotResponses()
                        ) : (
                          <div
                            className={styles.loader}
                          >{`Loading ${channelName} Elements...`}</div>
                        )}
                      </div>
                      <div className={styles.botSaysReplies} />
                    </>
                  </div>
                )}
              </Accordion.Item>
            </Accordion>
          </div>
        )}

        {this.props.edit && this.props.new && (
          <div className={styles.entities}>
            <div className={styles.entitiesLabel}>Entity Type</div>
            {ENTITY_TYPES.map((entityTypeValue) => {
              return (
                <EntityTypeCard
                  key={entityTypeValue}
                  {...ENTITY_META_DATA[entityTypeValue]}
                  entityType={entityTypeValue}
                  onClick={() => this.props.onEntityTypeChange(entityTypeValue)}
                  selected={entityType === entityTypeValue}
                />
              );
            })}
          </div>
        )}
        {!this.props.new && (
          <div className={styles.entities}>
            <EntityTypeCard
              entityType={this.props.entityType}
              {...ENTITY_META_DATA[this.props.entityType]}
              selected
            />
          </div>
        )}
        <div>
          <Accordion useDragHandle>
            <Accordion.Item
              reorderDisabled
              key="advanced-options"
              index="advanced-options"
              active={this.state.advancedOptionsVisible}
              onChange={() => this._toggleAdvancedOptions()}
              noPadding
              renderHeader={() => (
                <div className={styles.advancedOptionsHeader}>
                  <Icon iconType="gear" /> Advanced Options
                </div>
              )}
            >
              {this.state.advancedOptionsVisible && (
                <div className={styles.advancedOptionsBody}>
                  {entityCategory === ENTITY_CATEGORY.DOMAIN &&
                    entityType === ENTITY_TYPE_REGEX && (
                      <Toggle
                        label="Skip Language Translation"
                        prefixClassName={styles.advancedOptionsBodyToggle}
                        value={disableTranslation}
                        tooltip={
                          <>
                            When handling user intents in different languages,
                            entity detection usually happens after the user's
                            message is translated into English. However, if you
                            enable the "Skip Language Translation" setting, then
                            the detection of this specific entity will take
                            place before the user's message is translated. In
                            simpler terms, entity detection will directly happen
                            in the user's native language.
                          </>
                        }
                        onChange={(value) =>
                          this.props.onEntityInfoChange(
                            'disableTranslation',
                            value
                          )
                        }
                        disabled={BotStore.isProductionEnv()}
                      />
                    )}
                  <Chips
                    className={styles.advancedOptionsBodyInput}
                    disabled={!this.props.edit}
                    label="Payload keys (api_name)"
                    hint="The key used when sending custom payloads in HSL elements"
                    value={payloadKey || []}
                    placeholder="Type and Press Enter to add..."
                    onChange={(value) =>
                      this.props.onEntityInfoChange('payloadKey', value)
                    }
                  />
                  <Chips
                    className={styles.advancedOptionsBodyInput}
                    disabled={!this.props.edit}
                    label="Context keys (completion_key)"
                    hint="The keyword used to map the value to this entity when using custom payload"
                    value={contextKey || []}
                    placeholder="Type and Press Enter to add..."
                    onChange={(value) =>
                      this.props.onEntityInfoChange('contextKey', value)
                    }
                  />
                  <Chips
                    className={styles.advancedOptionsBodyInput}
                    disabled={!this.props.edit}
                    label="Form keys (form_key)"
                    hint="Keyword used to map the value to this Entity when using Forms"
                    value={formKey || []}
                    placeholder="Type and Press Enter to add..."
                    onChange={(value) =>
                      this.props.onEntityInfoChange('formKey', value)
                    }
                  />
                  <Chips
                    className={styles.advancedOptionsBodyInput}
                    disabled={!this.props.edit}
                    label="Previous Context Tags (prev_context_tags)"
                    hint="Keyword used to run entity detection based on Previous Bot Response"
                    value={prevContextTags || []}
                    placeholder="Type and Press Enter to add..."
                    onChange={(value) =>
                      this.props.onEntityInfoChange('prevContextTags', value)
                    }
                  />
                </div>
              )}
            </Accordion.Item>
          </Accordion>
        </div>
      </div>
    );
  }

  _toggleEntityReprompt = () => {
    this.setState({
      entityRepromptVisible: !this.state.entityRepromptVisible,
    });
  };

  _getTabs() {
    if (this.props.isSuperman) {
      return this.props.supermanSupportedLanguages;
    }

    return [
      {
        title: 'English',
        path: 'en',
      },
      {
        title: 'Hindi',
        path: 'hi',
      },
    ];
  }

  _handleTabChange = (botSaysLanguage, hybridLanguage) => {
    const { updateCurrentChannelName } = this.props;
    dispatch(ActionTypes.UPDATE_ENTITY_LANGUAGES, {
      language: botSaysLanguage,
      hybridLanguage,
    });
    this.setState({
      language: botSaysLanguage,
      currentHybridLanguage: hybridLanguage,
      currentChannelId: 5,
      channelName: 'WebSDK',
    });
    updateCurrentChannelName('WebSDK');
  };

  _setRepromptState = (key, value) => {
    this.props.onEntityRepromptChange(
      key,
      value,
      this.state.language,
      this.state.currentHybridLanguage,
      this.state.currentChannelId
    );
  };

  _toggleAdvancedOptions = () => {
    this.setState({
      advancedOptionsVisible: !this.state.advancedOptionsVisible,
    });
  };

  validate = () => {
    const name = this.props.name.trim();
    const disableLanguageGuardrails = BotStore.getFeatureFlag(
      FEATURE_FLAG_DATA.FLAGS.DISABLE_LANGUAGE_GUARDRAILS
    );

    if (this.props.name.length < 3) {
      createAlert(
        'Please specify a longer Entity Name.',
        null,
        AlertTypes.NEGATIVE
      );
      return false;
    }

    const pattern = new RegExp('^[a-z][a-zs_]+[a-z]+$');
    if (!pattern.test(name)) {
      createAlert(
        'Entity Name can contain only small letters and underscore',
        null,
        AlertTypes.NEGATIVE
      );
      return false;
    }

    if (this.props.isSuperman) {
      const {
        entityRepromptValue,
        entityRepromptValueWithChannel,
        allHybridLanguages,
      } = this.props;
      const { language, currentChannelId } = this.state;
      const { variants } =
        entityRepromptValueWithChannel?.[language]?.[currentChannelId]
          ?.botSays || {};
      const hasEntityRepromptValue =
        Object.keys(entityRepromptValue).length > 0;
      if (!hasEntityRepromptValue && !disableLanguageGuardrails) {
        createAlert(
          'Please specify an Entity-reprompt Bot Message',
          null,
          AlertTypes.NEGATIVE
        );
        return false;
      }
      if (hasEntityRepromptValue && !disableLanguageGuardrails) {
        if (
          isBotResponseEmpty(allHybridLanguages, entityRepromptValue) ||
          (!variants?.length && currentChannelId === 5)
        ) {
          createAlert(
            'Entity-reprompt Bot Messages for all languages must be present',
            null,
            AlertTypes.NEGATIVE
          );
          return false;
        }
      }
    }

    if (this.props.new && this.props.existingEntityList.includes(name)) {
      createAlert(
        'Entity name has already been used. Please choose a different name',
        null,
        AlertTypes.NEGATIVE
      );
      return false;
    }

    const description = this.props.description.trim();
    if (description.length < 10) {
      createAlert(
        'Please describe the entity in more detail (Minimum 10 letters)',
        null,
        AlertTypes.NEGATIVE
      );
      return false;
    }
    return true;
  };
}

EntityInfo.propTypes = {
  edit: PropTypes.bool,
  new: PropTypes.bool,
  onEntityInfoChange: PropTypes.func,
  onEntityTypeChange: PropTypes.func.isRequired,

  name: PropTypes.string,
  description: PropTypes.string,
  disableTranslation: PropTypes.bool,
  entityCategory: PropTypes.string,
  payloadKey: PropTypes.arrayOf(PropTypes.string),
  contextKey: PropTypes.arrayOf(PropTypes.string),
  formKey: PropTypes.arrayOf(PropTypes.string),
  prevContextTags: PropTypes.arrayOf(PropTypes.string),
  entityType: PropTypes.string,
  existingEntityList: PropTypes.arrayOf(PropTypes.string),
  dpsvChannels: PropTypes.arrayOf(PropTypes.string),
  updateCurrentChannelName: PropTypes.func,
};
